import React from 'react';
import { emphasize, styled } from '@mui/material/styles';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Chip from '@mui/material/Chip';
import HomeIcon from '@mui/icons-material/Home';
import { useNavigate, useLocation } from 'react-router-dom';

const StyledBreadcrumb = styled(Chip)(({ theme }) => {
  return {
    backgroundColor: theme.palette.grey[100],
    height: theme.spacing(3),
    color: (theme.vars || theme).palette.text.primary,
    fontWeight: theme.typography.fontWeightRegular,
    '&:hover, &:focus': {
      backgroundColor: emphasize(theme.palette.grey[100], 0.06),
      ...theme.applyStyles('dark', {
        backgroundColor: emphasize(theme.palette.grey[800], 0.06),
      }),
    },
    '&:active': {
      boxShadow: theme.shadows[1],
      backgroundColor: emphasize(theme.palette.grey[100], 0.12),
      ...theme.applyStyles('dark', {
        backgroundColor: emphasize(theme.palette.grey[800], 0.12),
      }),
    },
    ...theme.applyStyles('dark', {
      backgroundColor: theme.palette.grey[800],
    }),
  };
}) as typeof Chip;

export interface BreadcrumbItem {
  label: string;
  path?: string;
  icon?: React.ReactElement;
}

interface BrandcrumbProps {
  items?: BreadcrumbItem[];
  showHome?: boolean;
  maxItems?: number;
  sx?: any;
}

const Brandcrumb: React.FC<BrandcrumbProps> = ({ 
  items = [], 
  showHome = true,
  maxItems = 8,
  sx = {}
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Generate breadcrumbs from URL if no custom items provided
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    if (items.length > 0) {
      return items;
    }

    const pathnames = location.pathname.split('/').filter(x => x);
    const breadcrumbs: BreadcrumbItem[] = [];

    // Add home breadcrumb
    if (showHome) {
      breadcrumbs.push({
        label: 'Home',
        path: '/',
        icon: <HomeIcon fontSize="small" />
      });
    }

    // Generate breadcrumbs from URL
    let accumulatedPath = '';
    pathnames.forEach((name, index) => {
      accumulatedPath += `/${name}`;
      
      // Skip if it's an ID (MongoDB-like IDs)
      if (!/^[0-9a-fA-F]{24}$/.test(name)) {
        const isLast = index === pathnames.length - 1;
        breadcrumbs.push({
          label: formatLabel(name),
          path: isLast ? undefined : accumulatedPath,
        });
      }
    });

    return breadcrumbs;
  };

  const formatLabel = (label: string): string => {
    return label
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const breadcrumbs = generateBreadcrumbs();

  const handleClick = (path?: string) => {
    if (path) {
      navigate(path);
    }
  };

  return (
    <div role="presentation" style={sx}>
      <Breadcrumbs 
        aria-label="breadcrumb" 
        maxItems={maxItems}
        itemsBeforeCollapse={2}
        itemsAfterCollapse={2}
      >
        {breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1;
          const isClickable = !isLast && item.path;

          if (isClickable) {
            return (
              <StyledBreadcrumb
                key={`${item.label}-${index}`}
                label={item.label}
                icon={item.icon}
                onClick={() => handleClick(item.path)}
                clickable
              />
            );
          }

          return (
            <StyledBreadcrumb
              key={`${item.label}-${index}`}
              label={item.label}
              icon={item.icon}
            //   onDelete={isLast ? () => {} : undefined}
            //   deleteIcon={isLast ? <ExpandMoreIcon /> : undefined}
            />
          );
        })}
      </Breadcrumbs>
    </div>
  );
};

export default Brandcrumb;